import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import SEOHead from '@/components/base/SEOHead';
import { movies, categories } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);

  const categoryInfo = useMemo(() => {
    if (!movie) return null;
    return categories.find((c) => c.slug === movie.category);
  }, [movie]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (m) =>
          m.id !== movie.id &&
          (m.category === movie.category ||
            m.tags.some((tag) => movie.tags.includes(tag))),
      )
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 text-lg">影片不存在</p>
            <Link
              to="/"
              className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const canonicalUrl = `https://mogutv.com/article/${movie.id}`;
  const categoryName = categoryInfo ? categoryInfo.name : '';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '蘑菇影视首页',
        item: 'https://mogutv.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://mogutv.com/category/${movie.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: movie.title,
        item: canonicalUrl,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description.substring(0, 200),
    image: movie.imageUrl,
    url: canonicalUrl,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    dateCreated: `${movie.year}`,
    contentRating: movie.rating.toString(),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${movie.title}可以在蘑菇影视免费在线观看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `可以。蘑菇影视为您提供《${movie.title}》高清免费在线观看服务，无需付费即可观看完整版内容。`,
        },
      },
      {
        '@type': 'Question',
        name: `${movie.title}的画质怎么样？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `蘑菇影视采用先进的视频编码技术，${movie.title}支持高清画质播放，流畅不缓冲，为您带来沉浸式观影体验。`,
        },
      },
      {
        '@type': 'Question',
        name: `蘑菇影视上还有哪些类似${movie.subcategory}类型的内容？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `蘑菇影视拥有丰富的${categoryName}资源库，除了${movie.title}之外，还有众多热门${movie.subcategory}类${categoryName}供您选择。您可以在分类页面浏览更多相关内容。`,
        },
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${movie.title} - 蘑菇影视高清免费在线观看`,
    description: movie.description.substring(0, 160),
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇影视',
      url: 'https://mogutv.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${movie.title} - 蘑菇影视高清免费在线观看`}
        description={movie.description.substring(0, 160)}
        canonicalUrl={canonicalUrl}
        ogType="video.movie"
        structuredData={{
          '@graph': [breadcrumbSchema, articleSchema, faqSchema, webPageSchema],
        }}
      />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 pb-4 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                {t('common.breadcrumbHome')}
              </Link>
              <span>/</span>
              {categoryInfo && (
                <>
                  <Link
                    to={`/category/${categoryInfo.slug}`}
                    className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                  >
                    {categoryInfo.name}
                  </Link>
                  <span>/</span>
                </>
              )}
              <span className="text-foreground-900 font-medium line-clamp-1">
                {movie.title}
              </span>
            </nav>
          </div>
        </div>

        {/* Article Hero */}
        <section className="relative bg-background-100 pb-8">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 mx-auto lg:mx-0">
                <div className="w-48 md:w-56 aspect-[2/3] rounded-lg overflow-hidden">
                  <img
                    src={movie.imageUrl}
                    alt={movie.title}
                    title={`${movie.title} - 蘑菇影视高清免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap">
                    {movie.subcategory}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="w-0.5 h-4 bg-background-300"></span>
                  <span className="flex items-center gap-1">
                    <span className="text-accent-500 font-bold text-base">
                      {movie.rating}
                    </span>
                    <span className="text-xs text-foreground-500">{t('common.rating')}</span>
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 w-12 shrink-0">
                      {t('article.director')}
                    </span>
                    <span className="text-sm text-foreground-800">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 w-12 shrink-0">
                      {t('article.actors')}
                    </span>
                    <span className="text-sm text-foreground-800">
                      {movie.actors.join(' / ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 w-12 shrink-0">
                      {t('article.duration')}
                    </span>
                    <span className="text-sm text-foreground-800">{movie.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 w-12 shrink-0">
                      {t('article.region')}
                    </span>
                    <span className="text-sm text-foreground-800">{movie.region}</span>
                  </div>
                  {movie.episodes && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 w-12 shrink-0">
                        {t('article.episodes')}
                      </span>
                      <span className="text-sm text-foreground-800">
                        {movie.episodes}集
                      </span>
                    </div>
                  )}
                  {movie.updateStatus && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 w-12 shrink-0">
                        {t('article.updateStatus')}
                      </span>
                      <span className="text-sm text-primary-500 font-medium">
                        {movie.updateStatus}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-fill text-lg"></i>
                  </div>
                  {t('common.play')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
              {t('article.description')}
            </h2>
            <div className="bg-background-100 rounded-lg p-5 md:p-6">
              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {movie.description}
              </p>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
              <h2 className="text-xl font-heading font-bold text-foreground-900">
                {t('article.relatedRecommend')}
              </h2>
            </div>
            {relatedMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} size="small" />
                ))}
              </div>
            ) : (
              <p className="text-sm text-foreground-500">暂无相关推荐</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}